import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { MdArrowBack, MdCalendarToday, MdLocationOn, MdPayment, MdLogin, MdClose } from 'react-icons/md';
import api from '../services/api';
import { getUser } from '../services/auth';
import SubtleGridBackground from '../components/SubtleGridBackground';
import { t } from '../utils/i18n';

export default function JobPublicPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentUser = getUser();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showContactLoginMessage, setShowContactLoginMessage] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/jobs/public/${id}`);
        if (!cancelled) setJob(res.data?.job || null);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || e?.message || 'Greška pri učitavanju');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [id]);

  const city = job?.location?.city || '';
  const address = job?.location?.street || job?.location?.address || '';
  const whenText = useMemo(() => {
    if (!job?.scheduledDate) return null;
    try {
      const d = new Date(job.scheduledDate);
      return d.toLocaleDateString();
    } catch {
      return null;
    }
  }, [job?.scheduledDate]);

  const handleContact = () => {
    if (!currentUser) {
      setShowContactLoginMessage(true);
      return;
    }
    // Messaging flow lives in dashboard; keep it simple for now.
    navigate('/dashboard/conversations');
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <SubtleGridBackground />
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <div className="flex items-center justify-between gap-3 mb-6">
          <button
            type="button"
            onClick={() => window.history.length > 1 ? navigate(-1) : navigate('/')}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-xl bg-white/90 dark:bg-gray-800/90 border border-gray-200 dark:border-gray-700 shadow-sm hover:bg-white dark:hover:bg-gray-800 transition-colors"
          >
            <MdArrowBack className="text-lg" />
            <span className="text-sm font-semibold">{t('back') || 'Nazad'}</span>
          </button>
          <Link
            to="/"
            className="text-sm font-semibold text-primary-700 dark:text-primary-300 hover:underline"
          >
            {t('home') || 'Početna'}
          </Link>
        </div>

        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-3xl shadow-lg overflow-hidden">
          <div className="p-6 sm:p-8">
            {loading ? (
              <div className="animate-pulse space-y-3">
                <div className="h-7 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
                <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded" />
              </div>
            ) : error ? (
              <div className="rounded-2xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 p-4">
                <div className="font-semibold text-red-700 dark:text-red-200">{error}</div>
              </div>
            ) : !job ? (
              <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 p-6 text-center">
                <div className="font-semibold text-gray-800 dark:text-gray-200">Oglas nije pronađen.</div>
              </div>
            ) : (
              <>
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="min-w-0">
                    <h1 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-gray-100 leading-tight">
                      {job.title || 'Oglas'}
                    </h1>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600 dark:text-gray-300">
                      {(city || address) && (
                        <span className="inline-flex items-center gap-1">
                          <MdLocationOn className="text-base text-gray-400" />
                          <span className="truncate">{[address, city].filter(Boolean).join(', ')}</span>
                        </span>
                      )}
                      {whenText && (
                        <span className="inline-flex items-center gap-1">
                          <MdCalendarToday className="text-base text-gray-400" />
                          {whenText}
                        </span>
                      )}
                      {job.hourlyRate != null && (
                        <span className="inline-flex items-center gap-1">
                          <MdPayment className="text-base text-gray-400" />
                          <span className="font-semibold text-primary-700 dark:text-primary-300 tabular-nums">
                            {Number(job.hourlyRate).toLocaleString()} RSD/h
                          </span>
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex-shrink-0">
                    <button
                      type="button"
                      onClick={handleContact}
                      className="btn-primary px-6 py-3 text-base font-semibold"
                    >
                      {t('contact') || 'Kontaktiraj'}
                    </button>
                  </div>
                </div>

                {showContactLoginMessage && (
                  <div className="mt-5 rounded-2xl bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 p-4">
                    <p className="text-primary-800 dark:text-primary-200 font-medium mb-3">
                      Morate da se ulogujete da biste razmenjivali poruke sa korisnikom.
                    </p>
                    <div className="flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => navigate('/login', { state: { from: `/job/${id}` } })}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-primary-600 text-white font-semibold hover:bg-primary-700"
                      >
                        <MdLogin className="text-lg" /> Prijava
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowContactLoginMessage(false)}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <MdClose className="text-lg" /> Zatvori
                      </button>
                    </div>
                  </div>
                )}

                {job.description && (
                  <div className="mt-6">
                    <h2 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-2">
                      Opis
                    </h2>
                    <p className="text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
                      {job.description}
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

